require("./storefront");

import Vue from "vue";
import store from "./store";
import { notify, trans, chunk } from "./functions";
import VueToast from "vue-toast-notification";
import vClickOutside from "v-click-outside";
import VPagination from "./components/VPagination.vue";
import HeaderSearch from "./components/layout/HeaderSearch.vue";
import SidebarCart from "./components/layout/SidebarCart";
import NewsletterPopup from "./components/layout/NewsletterPopup";
import NewsletterSubscription from "./components/layout/NewsletterSubscription";
import CookieBar from "./components/layout/CookieBar";
import LandscapeProducts from "./components/LandscapeProducts.vue";
import DynamicTab from "./components/home/DynamicTab";
import HomeFeatures from "./components/home/HomeFeatures.vue";
import FeaturedCategories from "./components/home/FeaturedCategories.vue";
import BannerThreeColumnFullWidth from "./components/home/BannerThreeColumnFullWidth.vue";
import ProductTabsOne from "./components/home/ProductTabsOne.vue";
import TopBrands from "./components/home/TopBrands.vue";
import FlashSaleAndVerticalProducts from "./components/home/FlashSaleAndVerticalProducts.vue";
import FlashSale from "./components/home/FlashSale.vue";
import BannerTwoColumn from "./components/home/BannerTwoColumn.vue";
import VerticalProducts from "./components/home/VerticalProducts.vue";
import ProductGrid from "./components/home/ProductGrid.vue";
import BannerThreeColumn from "./components/home/BannerThreeColumn.vue";
import ProductTabsTwo from "./components/home/ProductTabsTwo.vue";
import BannerOneColumn from "./components/home/BannerOneColumn.vue";
import ProductIndex from "./components/products/Index";
import ProductCardGridView from "./components/products/index/ProductCardGridView.vue";
import ProductCardListView from "./components/products/index/ProductCardListView.vue";
import ProductCardVertical from "./components/ProductCardVertical.vue";
import ProductShow from "./components/products/Show";
import CartIndex from "./components/cart/Index";
import CheckoutCreate from "./components/checkout/Create";
import CompareIndex from "./components/compare/Index";
import MyWishlist from "./components/account/wishlist/Index";
import MyAddresses from "./components/account/addresses/Index";

const FRONTEND_ERROR_LOG_ENDPOINT = "/core/api/frontend-errors";
const FRONTEND_ERROR_MAX_PER_PAGE = 10;
const FRONTEND_ERROR_TTL = 60 * 1000;

const frontendErrorReporterState = {
    sentCount: 0,
    fingerprints: new Map(),
};

function getFrontendErrorEndpoint() {
    if (typeof window.normalizeStorefrontAjaxUrl === "function") {
        return window.normalizeStorefrontAjaxUrl(FRONTEND_ERROR_LOG_ENDPOINT);
    }

    return FRONTEND_ERROR_LOG_ENDPOINT;
}

function normalizeFrontendErrorMessage(message) {
    if (typeof message === "string" && message.trim() !== "") {
        return message.trim();
    }

    return null;
}

function matchesIgnoredFrontendErrorUrl(url) {
    if (typeof url !== "string" || url.trim() === "") {
        return false;
    }

    const ignoredHosts = [
        "translate.google.com",
        "consent.cookiefirst.com",
        "connect.facebook.net",
        "googleads.g.doubleclick.net",
        "www.googleadservices.com",
        "www.googletagmanager.com",
    ];

    try {
        const parsedUrl = new URL(url, window.location.href);

        return ignoredHosts.some(
            (host) =>
                parsedUrl.hostname === host ||
                parsedUrl.hostname.endsWith(`.${host}`)
        );
    } catch (error) {
        return false;
    }
}

function shouldIgnoreFrontendError(payload) {
    const message = payload?.message || "";
    const sourceFile = payload?.source_file || "";
    const resourceUrl = payload?.resource_url || "";
    const ignoredMessages = [
        "ResizeObserver loop limit exceeded",
        "Script error.",
        "TypeError: undefined is not an object (evaluating 'window.webkit.messageHandlers')",
    ];
    const ignoredSources = [
        "chrome-extension://",
        "moz-extension://",
        "safari-extension://",
    ];

    if (ignoredMessages.includes(message)) {
        return true;
    }

    if (
        payload?.type === "resource-error" &&
        matchesIgnoredFrontendErrorUrl(resourceUrl)
    ) {
        return true;
    }

    if (
        message.includes("emailInput.value") &&
        matchesIgnoredFrontendErrorUrl(sourceFile)
    ) {
        return true;
    }

    if (
        message.includes("Cannot read properties of null (reading 'value')") &&
        matchesIgnoredFrontendErrorUrl(sourceFile)
    ) {
        return true;
    }

    return ignoredSources.some(
        (source) =>
            sourceFile.startsWith(source) || resourceUrl.startsWith(source)
    );
}

function createFrontendErrorFingerprint(payload) {
    return [
        payload.message || "",
        payload.source_file || "",
        payload.resource_url || "",
        payload.line || "",
        payload.column || "",
        payload.component || "",
        payload.current_url || "",
    ].join("|");
}

function canReportFrontendError(payload) {
    if (frontendErrorReporterState.sentCount >= FRONTEND_ERROR_MAX_PER_PAGE) {
        return false;
    }

    const now = Date.now();
    const fingerprint = createFrontendErrorFingerprint(payload);
    const lastSentAt =
        frontendErrorReporterState.fingerprints.get(fingerprint) || 0;

    frontendErrorReporterState.fingerprints.forEach((timestamp, key) => {
        if (now - timestamp > FRONTEND_ERROR_TTL) {
            frontendErrorReporterState.fingerprints.delete(key);
        }
    });

    if (lastSentAt && now - lastSentAt < FRONTEND_ERROR_TTL) {
        return false;
    }

    frontendErrorReporterState.fingerprints.set(fingerprint, now);
    frontendErrorReporterState.sentCount += 1;

    return true;
}

function serializeUnhandledReason(reason) {
    if (reason instanceof Error) {
        return {
            message: reason.message,
            stack: reason.stack || null,
        };
    }

    if (typeof reason === "string") {
        return {
            message: reason,
            stack: null,
        };
    }

    try {
        return {
            message: JSON.stringify(reason),
            stack: null,
        };
    } catch (error) {
        return {
            message: String(reason),
            stack: null,
        };
    }
}

function reportFrontendError(payload = {}) {
    const message = normalizeFrontendErrorMessage(payload.message);

    if (!message) {
        return;
    }

    const normalizedPayload = {
        type: payload.type || "error",
        message,
        stack: payload.stack || null,
        source_file: payload.source_file || null,
        resource_url: payload.resource_url || null,
        current_url: payload.current_url || window.location.href,
        line: payload.line || null,
        column: payload.column || null,
        component: payload.component || null,
        vue_info: payload.vue_info || null,
    };

    if (
        shouldIgnoreFrontendError(normalizedPayload) ||
        !canReportFrontendError(normalizedPayload)
    ) {
        return;
    }

    const csrfToken =
        typeof window.resolveCsrfToken === "function"
            ? window.resolveCsrfToken()
            : document
                  .querySelector('meta[name="csrf-token"]')
                  ?.getAttribute("content");

    fetch(getFrontendErrorEndpoint(), {
        method: "POST",
        credentials: "same-origin",
        keepalive: true,
        headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            ...(csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {}),
        },
        body: JSON.stringify(normalizedPayload),
    }).catch(() => {});
}

window.reportFrontendError = reportFrontendError;

Vue.prototype.route = route;
Vue.prototype.$notify = notify;
Vue.prototype.$trans = trans;
Vue.prototype.$chunk = chunk;

Vue.use(VueToast);
Vue.use(vClickOutside);

Vue.component("v-pagination", VPagination);
Vue.component("header-search", HeaderSearch);
Vue.component("sidebar-cart", SidebarCart);
Vue.component("newsletter-popup", NewsletterPopup);
Vue.component("newsletter-subscription", NewsletterSubscription);
Vue.component("cookie-bar", CookieBar);
Vue.component("landscape-products", LandscapeProducts);
Vue.component("dynamic-tab", DynamicTab);
Vue.component("home-features", HomeFeatures);
Vue.component("featured-categories", FeaturedCategories);
Vue.component("banner-three-column-full-width", BannerThreeColumnFullWidth);
Vue.component("product-tabs-one", ProductTabsOne);
Vue.component("top-brands", TopBrands);
Vue.component("flash-sale-and-vertical-products", FlashSaleAndVerticalProducts);
Vue.component("flash-sale", FlashSale);
Vue.component("banner-two-column", BannerTwoColumn);
Vue.component("vertical-products", VerticalProducts);
Vue.component("product-grid", ProductGrid);
Vue.component("banner-three-column", BannerThreeColumn);
Vue.component("product-tabs-two", ProductTabsTwo);
Vue.component("banner-one-column", BannerOneColumn);
Vue.component("product-index", ProductIndex);
Vue.component("product-card-grid-view", ProductCardGridView);
Vue.component("product-card-list-view", ProductCardListView);
Vue.component("product-card-vertical", ProductCardVertical);
Vue.component("product-show", ProductShow);
Vue.component("cart-index", CartIndex);
Vue.component("checkout-create", CheckoutCreate);
Vue.component("compare-index", CompareIndex);
Vue.component("my-wishlist", MyWishlist);
Vue.component("my-addresses", MyAddresses);

if (process.env.MIX_ENV_MODE === 'production') {
    Vue.config.devtools = false;
    Vue.config.debug = false;
    Vue.config.silent = true;
}

new Vue({
    el: "#app",

    computed: {
        cart() {
            return store.state.cart;
        },

        wishlistCount() {
            return store.wishlistCount();
        },
    },
});

window.refreshCart = () => {
    return $.ajax({
        method: "GET",
        url: route("cart.json"),
    }).then((cart) => {
        Shop.cart = cart;
        store.updateCart(cart);
        return cart;
    });
};

window.normalizeStorefrontAjaxUrl = (url) => {
    if (typeof url !== "string" || url.trim() === "") {
        return url;
    }

    try {
        const parsedUrl = new URL(url, window.location.href);
        const currentOrigin = window.location.origin;
        const configuredOrigin = window.Shop?.baseUrl
            ? new URL(window.Shop.baseUrl, window.location.href).origin
            : null;
        const shouldUseCurrentOrigin =
            ["http:", "https:"].includes(parsedUrl.protocol) &&
            (
                parsedUrl.origin === currentOrigin ||
                parsedUrl.origin === configuredOrigin ||
                ["127.0.0.1", "localhost"].includes(parsedUrl.hostname)
            );

        if (!shouldUseCurrentOrigin) {
            return url;
        }

        return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    } catch (error) {
        return url;
    }
};

window.resolveCsrfToken = () => {
    const metaToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

    return metaToken || window.Shop?.csrfToken || "";
};

$.ajaxPrefilter((options, originalOptions, jqXHR) => {
    options.url = window.normalizeStorefrontAjaxUrl(options.url);

    const csrfToken = window.resolveCsrfToken();

    if (csrfToken) {
        jqXHR.setRequestHeader("X-CSRF-TOKEN", csrfToken);
    }

    jqXHR.setRequestHeader("X-Requested-With", "XMLHttpRequest");
});

Vue.config.errorHandler = (error, vm, info) => {
    reportFrontendError({
        type: "vue-error",
        message: error?.message || info || "Vue error",
        stack: error?.stack || null,
        source_file: vm?.$options?.__file || null,
        component: vm?.$options?.name || vm?.$options?._componentTag || null,
        vue_info: info || null,
    });

    if (process.env.MIX_ENV_MODE !== "production") {
        console.error(error);
    }
};

window.addEventListener(
    "error",
    (event) => {
        const target = event.target;
        const isResourceError = target && target !== window && target.tagName;

        if (isResourceError) {
            reportFrontendError({
                type: "resource-error",
                message: `Failed to load ${String(
                    target.tagName
                ).toLowerCase()} asset`,
                resource_url:
                    target.currentSrc || target.src || target.href || null,
                source_file: window.location.href,
            });

            return;
        }

        reportFrontendError({
            type: "error",
            message: event.message || "Unknown JS error",
            stack: event.error?.stack || null,
            source_file: event.filename || null,
            line: event.lineno || null,
            column: event.colno || null,
        });
    },
    true
);

window.addEventListener("unhandledrejection", (event) => {
    const serializedReason = serializeUnhandledReason(event.reason);

    reportFrontendError({
        type: "unhandledrejection",
        message: serializedReason.message || "Unhandled promise rejection",
        stack: serializedReason.stack || null,
    });
});
